// Throwaway: one-shot Program Product Reconcile feature. v2 adds per-row Delete
// (on Orphan rows) and Add (on Missing rows). Backed by ProgramProductReconcileController
// on the server, which queries the legacy Uniformax DB. Remove this file + the module
// declaration + the template wiring + the menu entry + the ViewEnum value when
// the cleanup work is finished.
//
// NOTE on field casing: the server uses Breeze's custom JSON serializer settings
// which keep PascalCase property names. So the wire DTO uses PascalCase, not the
// usual Angular camelCase.
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AgFns, GridOptionsExt, UtilDialogService } from '@core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env';
import { ProgramFrmComponent } from './program-frm.component';
import { ToastrService } from 'ngx-toastr';
import { GridReadyEvent } from '@ag-grid-community/core';

// Throwaway: matches the key expected by ProgramProductReconcileController on
// the server. Both sides must stay in sync until this feature is deleted.
const RECONCILE_KEY = 'Back2Zero100!';
const RECONCILE_HEADERS = new HttpHeaders({ 'X-Reconcile-Key': RECONCILE_KEY });

interface ProximityRow {
  ProductTypeConfigId: string;
  PricedProductTypeId: string;
  ConfigName: string;
  ManufacturerName: string;
  SupplierName: string;
  ProductKey: string;
  ProductName: string;
  IsDiscontinued: boolean;
  Status: number; // 0 = Match, 1 = Orphan, 2 = Unconstrained (RowStatus enum)
  OrderRefCount: number;
  AccountIssuanceRefCount: number;
  ProgramIssuanceRefCount: number;
}

interface UniformaxRow {
  ManufacturerName: string;
  ProductKey: string;
  ConfigCatalogue: string;
  IssuanceNames: string;
  PilLimitDays: number;
  PilLimitMin: number;
  PilLimitMax: number;
  PriMinimum: number;
  PriMaximum: number;
  PriOtUnits: number;
  PriOtDays: number;
  isMissing?: boolean; // client-set; not from server
}

interface ReconcileDto {
  ProgramId: string;
  AccountName?: string;
  ProgramName?: string;
  Unmatched: boolean;
  UnmatchedReason?: string;
  ProximityRows: ProximityRow[];
  UniformaxRows: UniformaxRow[];
  MissingRowKeys: string[];
  MatchCount: number;
  OrphanCount: number;
  MissingCount: number;
  UnconstrainedCount: number;
  IsIssuanceOnly: boolean;
}

interface MutationResult {
  Success: boolean;
  Error?: string;
}

interface BulkSkippedRow {
  ConfigName: string;
  ManufacturerName: string;
  ProductKey: string;
  Reason: string;
}

interface BulkRemoveResult {
  DeletedCount: number;
  SkippedCount: number;
  Skipped: BulkSkippedRow[];
  Error?: string;
}

@Component({
  selector: 'prox-program-reconcile-sub',
  templateUrl: './program-reconcile.sub.component.html',
  standalone: false,
})
export class ProgramReconcileSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  hasLoaded = false;
  loading = false;
  dto?: ReconcileDto;

  proxGridOptions: GridOptionsExt = {};
  ufxGridOptions: GridOptionsExt = {};

  private isOrphan(r: ProximityRow): boolean {
    return r?.Status === 1;
  }

  private isUnconstrained(r: ProximityRow): boolean {
    return r?.Status === 2;
  }

  // Rows the operator can delete (Orphans or Unconstrained). Both run the same
  // server cascade — only their reasons-for-deletion differ.
  private isDeletable(r: ProximityRow): boolean {
    return this.isOrphan(r) || this.isUnconstrained(r);
  }

  private statusLabel(r: ProximityRow): string {
    if (this.isOrphan(r)) return 'ORPHAN';
    if (this.isUnconstrained(r)) return 'UNCONSTRAINED';
    return 'Match';
  }

  // Only Order references block deletion now. Issuance references no longer block:
  // the cascade snips the ProgramProductTypeTagMap rows so issuance rules stay
  // intact (they just no longer cover this product via the tag chain).
  private isBlocked(r: ProximityRow): boolean {
    return (r?.OrderRefCount ?? 0) > 0;
  }

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private dialogService: UtilDialogService,
  ) {}

  ngOnInit() {
    this.proxGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onProxGridReady,
      rowModelType: 'clientSide',
      // Stable row id prevents an ag-Grid rendering artifact (phantom blank row)
      // when the clientSide model has many rows sharing the same sort-key value.
      getRowId: (p) => (p.data as ProximityRow).ProductTypeConfigId,
      getRowStyle: (params) => {
        const r = params.data as ProximityRow;
        if (r && this.isDeletable(r)) {
          return { background: '#fee2e2' }; // red-100 — both Orphan and Unconstrained
        }
        return undefined;
      },
    });
    this.ufxGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onUfxGridReady,
      rowModelType: 'clientSide',
      // No natural primary key on UniformaxRowDto; rows repeat by (Catalog, PRI),
      // so include IssuanceNames (single PRI name now) in the row id.
      getRowId: (p) => {
        const r = p.data as UniformaxRow;
        return `${(r.ManufacturerName ?? '').trim()}|${(r.ProductKey ?? '').trim()}|${(r.ConfigCatalogue ?? '').trim()}|${(r.IssuanceNames ?? '').trim()}`;
      },
      getRowStyle: (params) => {
        const r = params.data as UniformaxRow;
        if (r?.isMissing) {
          return { background: '#dbeafe' }; // blue-100
        }
        return undefined;
      },
    });
  }

  ngOnChanges(_changes: SimpleChanges) {
    if (this.visible && !this.hasLoaded && this.parent?.program) {
      this.load();
    }
  }

  // Required by ISubComponent contract; load is gated by ngOnChanges.
  async refresh(_forceRefresh: boolean): Promise<void> {}

  async load() {
    this.loading = true;
    try {
      const url = environment.apiUrl + 'ProgramProductReconcile/Diff';
      const dto = await firstValueFrom(
        this.http.get<ReconcileDto>(url, {
          params: { programId: this.parent.program.id },
          headers: RECONCILE_HEADERS,
        }),
      );
      // Natural key is (Manufacturer | Catalogue) — must mirror NormalizeKey() on the server.
      const missingSet = new Set((dto.MissingRowKeys ?? []).map((k) => this.normKey(k)));
      dto.UniformaxRows = (dto.UniformaxRows ?? []).map((u) => ({
        ...u,
        isMissing: missingSet.has(this.normKey(`${u.ManufacturerName}|${u.ConfigCatalogue}`)),
      }));
      this.dto = dto;
      this.hasLoaded = true;
    } catch (e: any) {
      this.toastr.error(e?.error?.message ?? e?.message ?? 'Reconcile request failed', 'Reconcile');
    } finally {
      this.loading = false;
    }
  }

  async reload() {
    this.hasLoaded = false;
    await this.load();
  }

  private normKey(key: string): string {
    return (key ?? '')
      .split('|')
      .map((seg) => seg.trim())
      .join('|')
      .toLowerCase();
  }

  // Per-row Delete (Orphan or Unconstrained rows). Disabled when the row has order
  // references — the server re-validates so this is just a UI affordance, not the
  // safety net. Server-side RemoveOrphanAsync doesn't care which status flagged the
  // row; the cascade is identical for both.
  async onRemoveOrphan(row: ProximityRow): Promise<void> {
    if (!row || !this.isDeletable(row)) return;
    if (this.isBlocked(row)) {
      this.toastr.warning('Row has order references; cannot remove.', 'Reconcile');
      return;
    }
    if (!this.isUnconstrained(row)) {
      const proceed = await this.dialogService.verifyProceedDialog(
        'Remove from program?',
        `This product is not in the Uniformax authoritative list.\n\nRemove "${row.ConfigName}" (${row.ManufacturerName} / ${row.ProductKey} — ${row.ProductName}) from this program?`,
      );
      if (!proceed) return;
    }

    try {
      const url = environment.apiUrl + 'ProgramProductReconcile/RemoveOrphan';
      const result = await firstValueFrom(
        this.http.post<MutationResult>(url, {
          programId: this.parent.program.id,
          productTypeConfigId: row.ProductTypeConfigId,
        }, { headers: RECONCILE_HEADERS }),
      );
      if (result?.Success) {
        this.toastr.success(`Removed ${row.ConfigName}.`, 'Reconcile');
        await this.reload();
      } else {
        this.toastr.error(result?.Error ?? 'Remove failed.', 'Reconcile');
      }
    } catch (e: any) {
      this.toastr.error(e?.error?.message ?? e?.message ?? 'Remove request failed', 'Reconcile');
    }
  }

  // Bulk Delete All Orphans. Server re-runs the diff and deletes every orphan
  // with zero FK references; blocked rows are reported back so the operator
  // can see what's left and why.
  async onRemoveAllOrphans(): Promise<void> {
    if (!this.dto || this.dto.OrphanCount === 0) return;
    const sample = this.dto.ProximityRows
      .filter((r) => this.isOrphan(r))
      .slice(0, 8)
      .map((r) => r.ConfigName)
      .join(', ');
    const more = this.dto.OrphanCount > 8 ? `, ... (${this.dto.OrphanCount - 8} more)` : '';
    const proceed = await this.dialogService.verifyProceedDialog(
      `Remove ${this.dto.OrphanCount} orphan row(s)?`,
      `This will remove every orphan row from this program. The product's tag-map links are also deleted so issuance rules no longer cover it via those tags. Rows with order references are skipped and reported.\n\nAffected: ${sample}${more}`,
    );
    if (!proceed) return;

    try {
      const url = environment.apiUrl + 'ProgramProductReconcile/RemoveAllOrphans';
      const result = await firstValueFrom(
        this.http.post<BulkRemoveResult>(url,
          { programId: this.parent.program.id },
          { headers: RECONCILE_HEADERS }),
      );
      if (result?.Error) {
        this.toastr.error(result.Error, 'Reconcile');
      } else if (result?.SkippedCount && result.SkippedCount > 0) {
        const lines = result.Skipped
          .slice(0, 5)
          .map((s) => `• ${s.ConfigName}: ${s.Reason}`)
          .join('\n');
        const moreLines = result.Skipped.length > 5 ? `\n• ... (${result.Skipped.length - 5} more)` : '';
        this.toastr.warning(
          `Deleted ${result.DeletedCount}, skipped ${result.SkippedCount}.\n${lines}${moreLines}`,
          'Reconcile',
          { enableHtml: false, timeOut: 10000 },
        );
      } else {
        this.toastr.success(`Removed ${result?.DeletedCount ?? 0} orphan row(s).`, 'Reconcile');
      }
      await this.reload();
    } catch (e: any) {
      this.toastr.error(e?.error?.message ?? e?.message ?? 'Bulk remove failed.', 'Reconcile');
    }
  }

  // Per-row Add (Missing Uniformax rows only). Server looks up an existing
  // ProductTypeConfig by (account, manufacturer, name) and inserts the join row.
  // If no matching config exists, the server returns an informative error.
  async onAddMissing(row: UniformaxRow): Promise<void> {
    if (!row?.isMissing) return;
    const proceed = await this.dialogService.verifyProceedDialog(
      'Add to program?',
      `Attach "${row.ConfigCatalogue.trim()}" (${row.ManufacturerName} / ${row.ProductKey}) to this program?`,
    );
    if (!proceed) return;

    try {
      const url = environment.apiUrl + 'ProgramProductReconcile/AddMissing';
      const result = await firstValueFrom(
        this.http.post<MutationResult>(url, {
          programId: this.parent.program.id,
          manufacturerName: row.ManufacturerName,
          configName: row.ConfigCatalogue.trim(),
        }, { headers: RECONCILE_HEADERS }),
      );
      if (result?.Success) {
        this.toastr.success(`Added ${row.ConfigCatalogue.trim()}.`, 'Reconcile');
        await this.reload();
      } else {
        this.toastr.error(result?.Error ?? 'Add failed.', 'Reconcile');
      }
    } catch (e: any) {
      this.toastr.error(e?.error?.message ?? e?.message ?? 'Add request failed', 'Reconcile');
    }
  }

  onProxGridReady = (event: GridReadyEvent) => {
    const colDefs = [
      {
        ...AgFns.createButtonProps('', this.onRemoveOrphan.bind(this), {
          icon: 'pi pi-trash',
          tooltip: 'Remove this row from the program. Also deletes the product’s tag-map links. Disabled when there are order references.',
          canDisplay: (r: ProximityRow) => this.isDeletable(r),
          calcDisabled: (r: ProximityRow) => this.isBlocked(r),
        }),
        ...AgFns.fixWidth(60),
      },
      { headerName: 'Supplier', field: 'SupplierName', ...AgFns.flexWidth(120) },
      { headerName: 'Manufacturer', field: 'ManufacturerName', ...AgFns.flexWidth(140) },
      { headerName: 'Product Key', field: 'ProductKey', ...AgFns.flexWidth(100) },
      { headerName: 'Config / Catalog', field: 'ConfigName', ...AgFns.flexWidth(120) },
      { headerName: 'Product Name', field: 'ProductName', ...AgFns.flexWidth(180) },
      {
        colId: 'statusRank',
        headerName: 'Status',
        // Orphan (0) first, Unconstrained (1) next, Match (2) last. Sort ascending.
        valueGetter: (p: any) => {
          if (this.isOrphan(p.data)) return 0;
          if (this.isUnconstrained(p.data)) return 1;
          return 2;
        },
        valueFormatter: (p: any) => {
          if (p.value === 0) return 'ORPHAN';
          if (p.value === 1) return 'UNCONSTRAINED';
          return 'Match';
        },
        sort: 'asc' as const,
        sortIndex: 0,
        ...AgFns.fixWidth(140),
      },
      { headerName: 'Orders', field: 'OrderRefCount', ...AgFns.fixWidth(80) },
      { headerName: 'Acct Issuances', field: 'AccountIssuanceRefCount', ...AgFns.fixWidth(110) },
      { headerName: 'Prog Issuances', field: 'ProgramIssuanceRefCount', ...AgFns.fixWidth(110) },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: 'statusRank', sort: 'asc' }]);
  };

  onUfxGridReady = (event: GridReadyEvent) => {
    const colDefs = [
      {
        ...AgFns.createButtonProps('', this.onAddMissing.bind(this), {
          icon: 'pi pi-plus',
          tooltip: 'Attach this row to the program.',
          canDisplay: (r: UniformaxRow) => !!r?.isMissing,
        }),
        ...AgFns.fixWidth(60),
      },
      { headerName: 'Manufacturer', field: 'ManufacturerName', ...AgFns.flexWidth(140) },
      { headerName: 'Product Key', field: 'ProductKey', ...AgFns.flexWidth(100) },
      { headerName: 'Catalog', field: 'ConfigCatalogue', ...AgFns.flexWidth(120) },
      { headerName: 'Issuance(s)', field: 'IssuanceNames', ...AgFns.flexWidth(140) },
      { headerName: 'Limit Days', field: 'PilLimitDays', ...AgFns.fixWidth(110) },
      { headerName: 'Limit Min', field: 'PilLimitMin', ...AgFns.fixWidth(110) },
      { headerName: 'Limit Max', field: 'PilLimitMax', ...AgFns.fixWidth(110) },
      { headerName: 'PRI Min', field: 'PriMinimum', ...AgFns.fixWidth(110) },
      { headerName: 'PRI Max', field: 'PriMaximum', ...AgFns.fixWidth(110) },
      { headerName: 'OT Units', field: 'PriOtUnits', ...AgFns.fixWidth(110) },
      { headerName: 'OT Days', field: 'PriOtDays', ...AgFns.fixWidth(110) },
      {
        colId: 'statusRank',
        headerName: 'Status',
        // Missing first (rank 0), then matches (rank 1). Sort ascending.
        valueGetter: (p: any) => (p.data?.isMissing ? 0 : 1),
        valueFormatter: (p: any) => (p.value === 0 ? 'MISSING in Proximity' : 'Match'),
        sort: 'asc' as const,
        sortIndex: 0,
        ...AgFns.fixWidth(160),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: 'statusRank', sort: 'asc' }]);
  };
}
